export type PhoneMessageDirection = 'received' | 'sent'

export interface PhoneConversationMessage {
  id: string
  direction: PhoneMessageDirection
  speaker: string
  text: string
  time: string
}

export interface PhoneConversationGroup {
  id: string
  label: string
  messages: readonly PhoneConversationMessage[]
}

export interface PhoneConversationDefinition {
  id: string
  contact: string
  statusTime: string
  groups: readonly PhoneConversationGroup[]
}

export interface PhoneTextHistoryMessage {
  id: string
  direction: PhoneMessageDirection
  speaker: string
  text: string
  time?: string
}

export interface PhoneTextHistoryGroup {
  label: string
  messages: readonly PhoneTextHistoryMessage[]
}

export interface PhoneTextMessage {
  time?: string
  statusTime?: string
  currentLabel?: string | null
  history?: readonly PhoneTextHistoryGroup[]
  /** History message whose lower edge should peek into the initial phone view. */
  initialPeekMessageId?: string
}

export interface PhoneConversationOpenOptions {
  initialPeekMessageId?: string
}

export interface FocusedPhoneMessage {
  speaker: string
  text: string
  phoneMessage: PhoneTextMessage
}

const requiredText = (value: string, field: string): string => {
  const cleanValue = value.trim()
  if (!cleanValue) throw new Error(`Phone conversation ${field} cannot be empty.`)
  return cleanValue
}

/**
 * Validates a chronological phone thread and creates the UI view for the
 * message that a story cue should open on.
 */
export class PhoneConversation {
  private readonly definition: PhoneConversationDefinition

  public constructor(definition: PhoneConversationDefinition) {
    const messageIds = new Set<string>()
    const groupIds = new Set<string>()

    if (!definition.groups.length) {
      throw new Error('A phone conversation needs at least one message group.')
    }

    const groups = definition.groups.map((group, groupIndex) => {
      const id = requiredText(group.id, `group ${groupIndex + 1} id`)
      if (groupIds.has(id)) throw new Error(`Duplicate phone conversation group id: ${id}`)
      groupIds.add(id)

      if (!group.messages.length) {
        throw new Error(`Phone conversation group "${id}" needs at least one message.`)
      }

      return {
        id,
        label: requiredText(group.label, `group "${id}" label`),
        messages: group.messages.map((message, messageIndex) => {
          const messageId = requiredText(message.id, `message ${messageIndex + 1} id`)
          if (messageIds.has(messageId)) {
            throw new Error(`Duplicate phone conversation message id: ${messageId}`)
          }
          messageIds.add(messageId)

          return {
            id: messageId,
            direction: message.direction,
            speaker: requiredText(message.speaker, `message "${messageId}" speaker`),
            text: requiredText(message.text, `message "${messageId}" text`),
            time: requiredText(message.time, `message "${messageId}" time`),
          }
        }),
      }
    })

    this.definition = {
      id: requiredText(definition.id, 'id'),
      contact: requiredText(definition.contact, 'contact'),
      statusTime: requiredText(definition.statusTime, 'statusTime'),
      groups,
    }
  }

  public openAt(
    messageId: string,
    options: PhoneConversationOpenOptions = {},
  ): FocusedPhoneMessage {
    const cleanMessageId = requiredText(messageId, 'focused message id')
    const initialPeekMessageId = options.initialPeekMessageId === undefined
      ? undefined
      : requiredText(options.initialPeekMessageId, 'initial peek message id')

    for (let groupIndex = 0; groupIndex < this.definition.groups.length; groupIndex += 1) {
      const group = this.definition.groups[groupIndex]
      const messageIndex = group.messages.findIndex((message) => message.id === cleanMessageId)
      if (messageIndex < 0) continue

      const focusedMessage = group.messages[messageIndex]
      if (focusedMessage.direction !== 'received') {
        throw new Error(`Focused phone message "${cleanMessageId}" must be received.`)
      }

      const history: PhoneTextHistoryGroup[] = this.definition.groups
        .slice(0, groupIndex)
        .map((historyGroup) => this.toHistoryGroup(historyGroup))
      const earlierMessagesInCurrentGroup = group.messages.slice(0, messageIndex)

      if (earlierMessagesInCurrentGroup.length) {
        history.push({
          label: group.label,
          messages: earlierMessagesInCurrentGroup.map((message) => this.toHistoryMessage(message)),
        })
      }

      if (
        initialPeekMessageId
        && !history.some((historyGroup) => (
          historyGroup.messages.some((message) => message.id === initialPeekMessageId)
        ))
      ) {
        throw new Error(
          `Initial peek message "${initialPeekMessageId}" must occur before focused message "${cleanMessageId}".`,
        )
      }

      return {
        speaker: this.definition.contact,
        text: focusedMessage.text,
        phoneMessage: {
          time: focusedMessage.time,
          statusTime: this.definition.statusTime,
          currentLabel: earlierMessagesInCurrentGroup.length ? null : group.label,
          history,
          initialPeekMessageId,
        },
      }
    }

    throw new Error(
      `Phone conversation "${this.definition.id}" has no message with id "${cleanMessageId}".`,
    )
  }

  private toHistoryGroup(group: PhoneConversationGroup): PhoneTextHistoryGroup {
    return {
      label: group.label,
      messages: group.messages.map((message) => this.toHistoryMessage(message)),
    }
  }

  private toHistoryMessage(message: PhoneConversationMessage): PhoneTextHistoryMessage {
    return {
      id: message.id,
      direction: message.direction,
      speaker: message.speaker,
      text: message.text,
      time: message.time,
    }
  }
}
